import { createFileRoute } from "@tanstack/react-router";

import {
  BrandLockup,
  HeroSubtitle,
  PatentBadge,
} from "@/components/marketing/BrandLockup";
import FaqSection from "@/components/marketing/FaqSection";
import FinalCta from "@/components/marketing/FinalCta";
import HowItWorks from "@/components/marketing/HowItWorks";
import { marketingHead } from "@/components/marketing/marketingHead";
import ProductHero, { ProductShot } from "@/components/marketing/ProductHero";
import RelatedProducts from "@/components/marketing/RelatedProducts";
import CatalogEquation, {
  AUDIO_FILE,
  FileIcon,
} from "@/components/marketing/sections/CatalogEquation";
import LanguageBand from "@/components/marketing/sections/LanguageBand";
import ReachSection from "@/components/marketing/sections/ReachSection";
import SavingsSection from "@/components/marketing/sections/SavingsSection";
import { SAVINGS_ICONS } from "@/components/marketing/sections/savingsIcons";
import Testimonials from "@/components/marketing/sections/Testimonials";
import Footer from "@/components/nav/Footer";

export const Route = createFileRoute("/cleancut")({
  component: CleanCutPage,
  head: () =>
    marketingHead({
      title: "Clean Version Maker for Songs | CleanCut by Lutely",
      description:
        "No session files needed. No editing chops required. Upload an explicit track, get a clean version in minutes.",
      path: "/cleancut",
    }),
});

const PLACEMENTS = [
  {
    title: "Radio & Broadcast",
    body: "Be ready when stations and broadcasters need a clean version.",
    icon: (
      <>
        <path d="M5 10h14v9H5zM8 6l8-3M8 14h4M8 17h2" />
        <circle cx="16" cy="14.5" r="2.5" />
      </>
    ),
  },
  {
    title: "Sync & Licensing",
    body: "Never miss an opportunity for film, TV, advertising, gaming, and more.",
    icon: (
      <>
        <rect x="4" y="5" width="16" height="14" rx="2" />
        <path d="m10 9 5 3-5 3z" />
      </>
    ),
  },
  {
    title: "Brands & Retail",
    body: "Prepare music for stores, brands, and public spaces that need broader-audience versions.",
    icon: <path d="M4 9h16l-1-4H5zM6 9v10h12V9M9 19v-5h6v5" />,
  },
  {
    title: "Events & Venues",
    body: "Be ready for sports, schools, festivals, family-friendly environments, and public performances.",
    icon: <path d="M7 4v3M17 4v3M4 9h16M5 6h14v14H5zM8 13h3M13 13h3M8 16h3" />,
  },
  {
    title: "Social & Promotion",
    body: "Give campaigns and platforms more flexibility in how they use your music.",
    icon: <path d="M4 13h3l9 4V7l-9 4H4zM7 13v5h3l1-3M19 9c1 2 1 4 0 6" />,
  },
  {
    title: "Clean-content audiences",
    body: "Reach listeners and platforms where explicit content is not allowed or preferred.",
    icon: (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M3 12h18M12 3c3 3 4 6 4 9s-1 6-4 9M12 3c-3 3-4 6-4 9s1 6 4 9" />
      </>
    ),
  },
];

const FAQ = [
  {
    question: "Does CleanCut change the whole song?",
    answer:
      "No. It’s the same track, just with the explicit words and phrases removed from the vocals.",
  },
  {
    question: "Can I change the words CleanCut detects?",
    answer:
      "Yes. You can review the detected cut points and add or remove cuts before finalizing your clean version.",
  },
  {
    question: "Do I need the original session files?",
    answer:
      "No. Upload the finished explicit track and CleanCut will create a clean version without the original session files.",
  },
  {
    question: "Can I sell the clean version with my song?",
    answer:
      "Yes. You can include it as a downloadable asset in a Lutely Super Single.",
  },
];

function CleanCutPage() {
  return (
    <main className="flex flex-col bg-white">
      <ProductHero
        title={
          <>
            <BrandLockup mark="®">CleanCut</BrandLockup>
            <br />
            <HeroSubtitle>Automatic clean edits.</HeroSubtitle>
          </>
        }
        lede={
          <>
            No session files needed.
            <br />
            No editing chops required.
            <br />
            Upload an explicit track, get a clean version in minutes.
          </>
        }
        cta="Try CleanCut"
        proof={[
          "Automatic explicit-lyric removal",
          "Review every edit before export",
        ]}
        stage={
          <>
            <PatentBadge />
            <ProductShot
              src="/marketing/cleancut-editor.webp"
              alt="CleanCut editor"
              caption="Real Lutely product screen"
            />
          </>
        }
      />
      <HowItWorks
        title={
          <>
            Cleans are necessary.
            <br />
            We make it <span className="text-blue-600">simple.</span>
          </>
        }
        intro="Upload your explicit track and let CleanCut do the editing. If you want more control, fine-tune the cuts and customize your clean."
        steps={[
          {
            title: "Upload",
            body: "CleanCut finds explicit words and phrases and automatically mutes them in the vocal to create your clean version.",
          },
          {
            title: "Review & Customize",
            body: "Adjust existing edits or add new cuts with the CleanCut editor for greater control. Choose silence or set the effect used for your clean version.",
          },
          {
            title: "Save & Use",
            body: "Your explicit and clean versions stay connected inside Lutely’s Audiolab, so you always have access to both whenever you need them.",
          },
        ]}
      />
      <LanguageBand
        title={
          <>
            English <span>+</span> Spanish
          </>
        }
        copy="CleanCut works with explicit songs in English and Spanish, giving you the same automatic workflow in either language."
      />
      <SavingsSection
        kicker="Time + cost savings"
        title={
          <>
            Save the time.
            <br />
            <span>Keep the money.</span>
          </>
        }
        items={[
          { icon: SAVINGS_ICONS.lyrics, text: "No hunting through lyrics." },
          { icon: SAVINGS_ICONS.folder, text: "No reopening old sessions." },
          {
            icon: (
              <path d="M4 9v6M8 5v14M12 8v8M16 4v16M20 7v5M18 16l4 4M22 16l-4 4" />
            ),
            text: "No engineering required to manually edit every explicit word.",
          },
        ]}
        result={{
          title: "Minutes, not hours.",
          body: "CleanCut handles the editing in minutes. Fine-tune it only when you want more control.",
        }}
      />
      <ReachSection
        kicker="Why cleans matter"
        title={
          <>
            Clean music.
            <br />
            <span>More places to go.</span>
          </>
        }
        intro="A clean version makes the same song ready for more audiences, more environments, and more opportunities."
        items={PLACEMENTS}
      />
      <CatalogEquation
        kicker="Your catalog"
        title="Double your usable catalog."
        body="Every explicit track can have a clean version beside it. One song becomes two ready-to-use versions, giving you more flexibility without returning to the original session or rebuilding the track."
        left={{
          icons: <FileIcon>{AUDIO_FILE}</FileIcon>,
          label: (
            <>
              <em>1</em> track in
            </>
          ),
        }}
        right={{
          icons: (
            <>
              <FileIcon>{AUDIO_FILE}</FileIcon>
              <FileIcon>{AUDIO_FILE}</FileIcon>
            </>
          ),
          label: (
            <>
              <em>2</em> versions ready
            </>
          ),
        }}
      />
      <Testimonials
        kicker="CleanCut in the real world"
        title={
          <>
            Artists and labels are making <span>real money</span> with their
            cleans.
          </>
        }
        items={[
          {
            quote:
              "I knew I needed to get clean versions of my music, but I didn’t have access to all of my sessions. But with Lutely, all I needed was an MP3 or WAV file to get clean versions and deliver my music to state and federal prisons throughout the country.",
            name: "Benny the Butcher",
            role: "Artist",
            image: "/marketing/benny-avatar.webp",
          },
          {
            quote:
              "Lutely has taken the once time-consuming work of making radio edits and turned it into an on-demand, same-day task. The efficiency this has given us is immeasurable.",
            name: "Perfect Time Music Group",
            role: "Record Label",
            image: "/marketing/perfect-time-music-group.webp",
            logo: true,
          },
          {
            quote:
              "We use Lutely every day to generate radio edit and clean versions for our new releases and our back catalog. Having clean versions allows us to deliver music to more retailers… and make more money for our artists and partners.",
            name: "Next Records",
            role: "Record Label",
            image: "/marketing/next-records.webp",
            logo: true,
          },
        ]}
      />
      <FaqSection items={FAQ} />
      <RelatedProducts current="clean" />
      <FinalCta
        title="You make the music. Lutely handles what comes next."
        body="Prepare your tracks, build the release, and sell directly to fans from one connected platform."
      />
      <Footer topClassName="bg-[#050923]" />
    </main>
  );
}
